import {z} from 'zod/v4';
import {Dateish, objectIdStringZ4} from '@packages/utilities/zod';

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | {[key: string]: JsonValue};

export const JsonValue: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValue),
    z.record(z.string(), JsonValue),
  ])
);

export type SourceFileMetadata = z.infer<typeof SourceFileMetadata>;
export const SourceFileMetadata = z.record(z.string(), JsonValue);

/** Server-owned provenance for a generated file. */
export type SourceFileGeneration = z.infer<typeof SourceFileGeneration>;
export const SourceFileGeneration = z.object({
  type: z.string().min(1),
  taskId: z.string().min(1),
  collectionId: objectIdStringZ4.optional(),
});

export const sourceFileConstraints = {
  name: {
    maxLength: 255,
  },
  version: {
    min: 0,
    default: 0,
  },
  description: {
    maxLength: 1024,
  },
  isDraft: {
    default: false,
  },
  kind: {
    default: 'code' satisfies SourceFileKind,
  },
} as const;

export const SourceFileKind = z.enum(['skill', 'rule', 'code']);
export type SourceFileKind = z.infer<typeof SourceFileKind>;

/**
 * [libraryVersionId, librarySourceFileId] provenance for files installed from
 * a Shared Library.
 */
export const SourceFileOrigin = z.tuple([objectIdStringZ4, objectIdStringZ4]);
export type SourceFileOrigin = z.infer<typeof SourceFileOrigin>;

export type SourceFileKey = {folderId?: string | null; name: string};
export const sourceFileKey = (file: SourceFileKey): string =>
  `${file.folderId ?? ''}/${file.name}`;

export type SourceFile = z.infer<typeof SourceFile>;
export const SourceFile = z
  .object({
    id: objectIdStringZ4,
    createdAt: Dateish,
    updatedAt: Dateish,
    siteId: objectIdStringZ4,
    folderId: objectIdStringZ4.nullable(),
    name: z.string().min(1).max(sourceFileConstraints.name.maxLength),
    path: z.string().min(1).optional(),
    content: z.string(),
    kind: SourceFileKind,
    description: z
      .string()
      .max(sourceFileConstraints.description.maxLength)
      .optional(),
    origin: SourceFileOrigin.optional(),
    metadata: SourceFileMetadata.optional(),
    generation: SourceFileGeneration.optional(),
    isDraft: z.boolean().default(sourceFileConstraints.isDraft.default),
    publishedOn: Dateish.optional(),
    version: z
      .number()
      .min(sourceFileConstraints.version.min)
      .default(sourceFileConstraints.version.default),
  })
  .describe('An arbitrary text file scoped to a site.');

export type SourceFileFilter = Pick<SourceFile, 'siteId' | 'folderId' | 'name'>;

/**
 * Wire shape of a delete tombstone: identifies a row without carrying its
 * content, kind, or origin. Because a tombstone can't say what it points at,
 * the DB filter in `deleteManyByKeys` is the only thing stopping one from
 * destroying an Instructions-owned or library-installed record.
 */
export type SourceFileTombstone = z.infer<typeof SourceFileTombstone>;
export const SourceFileTombstone = SourceFile.pick({
  folderId: true,
  name: true,
});

/**
 * Cap on tombstones per sync request, on both the MPS and REST persist paths.
 * Bounds the `$or` clause `deleteManyByKeys` builds from them.
 */
export const MAX_DELETED_SOURCE_FILES_PER_SYNC = 500;

/**
 * Whether a sync path may tombstone this row. Both producers (the Designer
 * engine and the multiplayer store) must agree with the Mongo-side filter in
 * `deleteManyByKeys` (`kind: 'code'`, `origin: {$exists: false}`), so a new
 * kind or provenance field only has to be handled here.
 */
export const isTombstonableSourceFile = (
  file: Pick<SourceFile, 'kind' | 'origin'>
): boolean => file.kind === 'code' && file.origin === undefined;

export type DesignerSourceFile = z.infer<typeof DesignerSourceFile>;
export const DesignerSourceFile = SourceFile.pick({
  folderId: true,
  name: true,
  path: true,
  content: true,
  origin: true,
  metadata: true,
  isDraft: true,
  publishedOn: true,
}).extend({
  // Legacy MPS instances may omit kind while they cycle through a deployment.
  // Those records predate instruction files and are safe to interpret as code.
  kind: SourceFileKind.default(sourceFileConstraints.kind.default),
  id: objectIdStringZ4.optional(),
  // Preserve missing versions for the client's stale-record guard.
  version: SourceFile.shape.version.removeDefault().optional(),
  description: SourceFile.shape.description,
  createdAt: SourceFile.shape.createdAt.optional(),
  updatedAt: SourceFile.shape.updatedAt.optional(),
});

export type UpsertSourceFile = z.infer<typeof UpsertSourceFile>;
export const UpsertSourceFile = SourceFile.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  publishedOn: true,
})
  .partial()
  .required({siteId: true, name: true})
  .extend({
    // Prevent these fields from using their default values when not provided
    folderId: objectIdStringZ4.nullish(),
    isDraft: z.boolean().optional(),
    version: z.number().min(sourceFileConstraints.version.min).optional(),
  });
